import { useCallback, useLayoutEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppDispatch } from 'src/AppContext';
import { parse } from 'querystring';
import Button from 'src/components/Button';
import { reSendVerification } from 'src/services/api';
import { getURLInQueryString } from 'src/services/util';
import { Trans, useTranslation } from 'react-i18next';
import useCustomHistory from 'src/hooks/useCustomHistory';
import { useModal } from 'src/ModalContext';

export default function AccountComplete(): JSX.Element {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const history = useCustomHistory();
  const location = useLocation();
  const qs = location.search.replace(/^[?]/gi, '');
  const { email, beforeLoginEndpoint } = parse(qs);
  const { showModal } = useModal();
  const verificationResendUrl = getURLInQueryString(qs, 'verificationResendUrl');
  const onResend = useCallback(() => {
    reSendVerification(verificationResendUrl)
      .then(() => {
        showModal({
          message: t('account/complete/resend_verify_email_success'),
          success: () => {
            return;
          },
        });
      })
      .catch(() => {
        showModal({
          message: t('account/complete/resend_verify_email_fail'),
          success: () => {
            return;
          },
        });
      });
  }, [verificationResendUrl]);

  useLayoutEffect(() => {
    dispatch({ type: 'SET_GNB_LEFT', name: 'hidden' });
    dispatch({ type: 'SET_GNB_RIGHT', name: 'close' });
    dispatch({
      type: 'SET_GNB_RIGHT_CALLBACK',
      cb: () => {
        history.push((beforeLoginEndpoint ? beforeLoginEndpoint : '/login') as string, {}, ['beforeLoginEndpoint']);
      },
    });
    return () => {
      dispatch({ type: 'RESET_GNB_LEFT' });
      dispatch({ type: 'RESET_GNB_RIGHT' });
      dispatch({ type: 'RESET_GNB_RIGHT_CALLBACK' });
    };
  }, []);

  const onClickLogin = useCallback(
    (e) => {
      e.preventDefault();
      history.push((beforeLoginEndpoint ? beforeLoginEndpoint : '/login') as string, null, [
        'email',
        'verificationResendUrl',
        'beforeLoginEndpoint',
      ]);
    },
    [history, beforeLoginEndpoint],
  );

  return (
    <>
      <div className="container mt-6">
        <h1 className="N_20_R">{t('account/complete/title')}</h1>
        <div className="mt-7 N_14_R text-brown-grey">
          <Trans
            i18nKey="account/complete/subtitle_1"
            components={{ span: <span className="text-bluey-purple font-medium" /> }}
            values={{ email }}
          ></Trans>
        </div>
        <div className="mt-4 N_14_R text-brown-grey">
          <Trans
            i18nKey="account/complete/subtitle_2"
            components={{ span: <span className="text-greyish-brown font-medium" /> }}
            values={{ button: `[${t('account/complete/verify_email_button')}]` }}
          ></Trans>
        </div>
        <div className="mt-10">
          <Button title={t('account/complete/login_button')} theme="sub" onClick={(e) => onClickLogin(e)}></Button>
        </div>
        <div className="mt-6 flex justify-between">
          <span className="N_12_R text-greyish-brown">* {t('account/complete/not_receive_verify_email')}</span>
          <a className="N_12_M text-black underline" href="#" onClick={onResend}>
            {t('account/complete/resend_verify_email_button')}
          </a>
        </div>
      </div>
      <ul className="container mt-10 py-6 N_12_R text-brown-grey bg-pale-grey list-disc">
        <li>{t('account/complete/disclaimer_1')}</li>
        <li>{t('account/complete/disclaimer_2')}</li>
        <li>
          <div>
            <Trans
              i18nKey="account/complete/disclaimer_3"
              components={{ span: <span className="text-greyish-brown font-medium" /> }}
              values={{
                button: `[${t('account/complete/resend_verify_email_button')}]`,
              }}
            ></Trans>
          </div>
        </li>
        <li>{t('account/complete/disclaimer_4')}</li>
      </ul>
    </>
  );
}
